import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Checkbox,
  FormField,
  FormSelect,
  FormTextarea,
  Label,
} from "@/components/ui";
import { TagsInput } from "./TagsInput";
import {
  ContactFormSchema,
  CONTACT_KIND_LABELS,
  CONTACT_KINDS,
  type Contact,
  type ContactFormValues,
} from "@/api/types/contact";
import { useCreateContact, useUpdateContact, type ContactCreateInput } from "@/api/hooks/useContacts";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present when editing; absent when creating. */
  contact?: Contact | null;
}

const EMPTY_VALUES: ContactFormValues = {
  kind: "host",
  name: "",
  company: "",
  phone: "",
  email: "",
  instagram: "",
  source: "",
  warm: "cold",
  tags: [],
  notes: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  email_consent: false,
  sms_consent: false,
};

function toFormValues(contact: Contact): ContactFormValues {
  return {
    kind: contact.kind,
    name: contact.name,
    company: contact.company ?? "",
    phone: contact.phone ?? "",
    email: contact.email ?? "",
    instagram: contact.instagram ?? "",
    source: contact.source ?? "",
    warm: contact.warm,
    tags: contact.tags,
    notes: contact.notes ?? "",
    address_line1: contact.address_line1 ?? "",
    address_line2: contact.address_line2 ?? "",
    city: contact.city ?? "",
    state: contact.state ?? "",
    postal_code: contact.postal_code ?? "",
    email_consent: contact.email_consent ?? false,
    sms_consent: contact.sms_consent ?? false,
  };
}

/** Normalize form strings ("" for an untouched optional field) to null for the API. */
function toApiInput(values: ContactFormValues): ContactCreateInput {
  const blankToNull = (v: string | null | undefined) =>
    v && v.trim() !== "" ? v.trim() : null;

  return {
    kind: values.kind,
    name: values.name.trim(),
    company: blankToNull(values.company),
    phone: blankToNull(values.phone),
    email: blankToNull(values.email),
    instagram: blankToNull(values.instagram),
    source: blankToNull(values.source),
    warm: values.warm,
    tags: values.tags,
    notes: blankToNull(values.notes),
    address_line1: blankToNull(values.address_line1),
    address_line2: blankToNull(values.address_line2),
    city: blankToNull(values.city),
    state: blankToNull(values.state),
    postal_code: blankToNull(values.postal_code),
    email_consent: values.email_consent ?? false,
    sms_consent: values.sms_consent ?? false,
  };
}

export function ContactFormDialog({ open, onClose, contact }: ContactFormDialogProps) {
  const isEditing = !!contact;
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const isSaving = createContact.isPending || updateContact.isPending;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (open) {
      reset(contact ? toFormValues(contact) : EMPTY_VALUES);
    }
  }, [open, contact, reset]);

  const onSubmit = handleSubmit(async (values) => {
    const input = toApiInput(values);
    try {
      if (isEditing && contact) {
        await updateContact.mutateAsync({ id: contact.id, ...input });
        toastSuccess("Contact updated");
      } else {
        await createContact.mutateAsync(input);
        toastSuccess("Contact created");
      }
      onClose();
    } catch (err) {
      toastError("Couldn't save contact", getErrorMessage(err));
    }
  });

  return (
    <Dialog open={open} onClose={onClose} ariaLabel={isEditing ? "Edit contact" : "Add contact"}>
      <DialogContent size="lg">
        {/* noValidate: zod/RHF owns validation — without this, a browser's
            native constraint validation on type="email" silently blocks the
            submit event before handleSubmit (and its error state) ever runs. */}
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader onClose={onClose}>
            {isEditing ? "Edit contact" : "Add contact"}
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Name"
                required
                error={errors.name?.message}
                {...register("name")}
              />
              <FormSelect label="Kind" required error={errors.kind?.message} {...register("kind")}>
                {CONTACT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {CONTACT_KIND_LABELS[kind]}
                  </option>
                ))}
              </FormSelect>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Company" error={errors.company?.message} {...register("company")} />
              <FormSelect label="Warm / cold" required error={errors.warm?.message} {...register("warm")}>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
              </FormSelect>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Phone" type="tel" error={errors.phone?.message} {...register("phone")} />
              <FormField label="Email" type="email" error={errors.email?.message} {...register("email")} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Instagram" error={errors.instagram?.message} {...register("instagram")} />
              <FormField label="Source" error={errors.source?.message} {...register("source")} />
            </div>

            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Address</p>
              <FormField
                label="Address line 1"
                error={errors.address_line1?.message}
                {...register("address_line1")}
              />
              <FormField
                label="Address line 2"
                error={errors.address_line2?.message}
                {...register("address_line2")}
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField label="City" error={errors.city?.message} {...register("city")} />
                <FormField label="State" error={errors.state?.message} {...register("state")} />
                <FormField
                  label="Postal code"
                  error={errors.postal_code?.message}
                  {...register("postal_code")}
                />
              </div>
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Consent</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                <Checkbox label="Has consented to email" {...register("email_consent")} />
                <Checkbox label="Has consented to SMS" {...register("sms_consent")} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <TagsInput value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            <FormTextarea label="Notes" error={errors.notes?.message} {...register("notes")} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : isEditing ? "Save changes" : "Create contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
